import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import * as subscriptionsApi from '@/services/api/endpoints/subscriptions'
import {
  CancelSubscriptionPayload,
  CreateCheckoutPayload,
  Plan,
  Subscription,
  SubscriptionLimits,
  UpdateSubscriptionPayload,
} from '@/services/api/endpoints/subscriptions'
import * as careerExpertApi from '@/services/api/endpoints/careerExpert'
import { BookCareerExpertSessionPayload, CareerExpertBooking } from '@/services/api/endpoints/careerExpert'
import { ApiError } from '@/services/api/types'

interface SubscriptionState {
  bookingCareerExpertSession: boolean
  cancelling: boolean
  careerExpertBookings: CareerExpertBooking[]
  careerExpertBookingsLoading: boolean
  checkoutLoading: boolean
  lastCareerExpertBooking: CareerExpertBooking | null
  limits: SubscriptionLimits | null
  limitsLoading: boolean
  plans: Plan[]
  plansLoading: boolean
  portalLoading: boolean
  reactivating: boolean
  subscription: Subscription | null
  subscriptionLoading: boolean
  updatingPlan: boolean
}

const initialState: SubscriptionState = {
  bookingCareerExpertSession: false,
  cancelling: false,
  careerExpertBookings: [],
  careerExpertBookingsLoading: false,
  checkoutLoading: false,
  lastCareerExpertBooking: null,
  limits: null,
  limitsLoading: false,
  plans: [],
  plansLoading: false,
  portalLoading: false,
  reactivating: false,
  subscription: null,
  subscriptionLoading: false,
  updatingPlan: false,
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof ApiError ? error.message : fallback)

export const fetchPlans = createAsyncThunk('subscription/fetchPlans', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getPlans()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load plans'))
  }
})

// A user who has never subscribed may not have a Subscription row yet - treat a
// 404 as "no subscription" (Free) rather than surfacing it as an error.
export const fetchMySubscription = createAsyncThunk('subscription/fetchMe', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getMySubscription()
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return null
    return rejectWithValue(errorMessage(error, 'Failed to load subscription'))
  }
})

export const fetchSubscriptionLimits = createAsyncThunk('subscription/fetchLimits', async (_, { rejectWithValue }) => {
  try {
    return await subscriptionsApi.getSubscriptionLimits()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load subscription limits'))
  }
})

export const startCheckout = createAsyncThunk('subscription/checkout', async (payload: CreateCheckoutPayload, { rejectWithValue }) => {
  try {
    const session = await subscriptionsApi.createCheckoutSession(payload)
    window.location.href = session.url
    return session
  } catch (error) {
    const message = errorMessage(error, 'Failed to start checkout')
    toast.error(message)
    return rejectWithValue(message)
  }
})

// PATCH /subscriptions/me: Free -> paid re-routes through checkout (redirect),
// paid -> paid applies immediate proration and returns the updated subscription.
export const changePlan = createAsyncThunk('subscription/changePlan', async (payload: UpdateSubscriptionPayload, { rejectWithValue }) => {
  try {
    const result = await subscriptionsApi.updateSubscription(payload)
    if ('url' in result) {
      window.location.href = result.url
      return null
    }
    toast.success('Subscription updated')
    return result
  } catch (error) {
    const message = errorMessage(error, 'Failed to update subscription')
    toast.error(message)
    return rejectWithValue(message)
  }
})

export const cancelMySubscription = createAsyncThunk(
  'subscription/cancel',
  async (payload: CancelSubscriptionPayload, { rejectWithValue }) => {
    try {
      const subscription = await subscriptionsApi.cancelSubscription(payload)
      toast.success(payload.immediately ? 'Subscription cancelled' : "Subscription will end at your current period's close")
      return subscription
    } catch (error) {
      const message = errorMessage(error, 'Failed to cancel subscription')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const reactivateMySubscription = createAsyncThunk('subscription/reactivate', async (_, { rejectWithValue }) => {
  try {
    const subscription = await subscriptionsApi.reactivateSubscription()
    toast.success('Subscription resumed')
    return subscription
  } catch (error) {
    const message = errorMessage(error, 'Failed to resume subscription')
    toast.error(message)
    return rejectWithValue(message)
  }
})

// Pro students get a confirmed booking back with no redirect (no Stripe
// involved); Free/Basic students get a one-time checkout session to redirect to.
export const bookCareerExpertSession = createAsyncThunk(
  'subscription/bookCareerExpertSession',
  async (payload: BookCareerExpertSessionPayload, { rejectWithValue }) => {
    try {
      const result = await careerExpertApi.bookCareerExpertSession(payload)
      if ('url' in result) {
        window.location.href = result.url
        return null
      }
      toast.success('Session booked - included with your Pro membership')
      return result
    } catch (error) {
      const message = errorMessage(error, 'Failed to book Career Expert session')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

export const fetchMyCareerExpertBookings = createAsyncThunk(
  'subscription/fetchCareerExpertBookings',
  async (_, { rejectWithValue }) => {
    try {
      return await careerExpertApi.listMyCareerExpertBookings()
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load Career Expert sessions'))
    }
  },
)

export const openBillingPortal = createAsyncThunk('subscription/billingPortal', async (returnUrl: string, { rejectWithValue }) => {
  try {
    const session = await subscriptionsApi.getBillingPortalUrl(returnUrl)
    window.location.href = session.url
    return session
  } catch (error) {
    const message = errorMessage(error, 'Failed to open billing portal')
    toast.error(message)
    return rejectWithValue(message)
  }
})

const subscriptionSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.plansLoading = true
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.plans = action.payload
        state.plansLoading = false
      })
      .addCase(fetchPlans.rejected, (state) => {
        state.plansLoading = false
      })
      .addCase(fetchMySubscription.pending, (state) => {
        state.subscriptionLoading = true
      })
      .addCase(fetchMySubscription.fulfilled, (state, action) => {
        state.subscription = action.payload
        state.subscriptionLoading = false
      })
      .addCase(fetchMySubscription.rejected, (state) => {
        state.subscriptionLoading = false
      })
      .addCase(fetchSubscriptionLimits.pending, (state) => {
        state.limitsLoading = true
      })
      .addCase(fetchSubscriptionLimits.fulfilled, (state, action) => {
        state.limits = action.payload
        state.limitsLoading = false
      })
      .addCase(fetchSubscriptionLimits.rejected, (state) => {
        state.limitsLoading = false
      })
      .addCase(startCheckout.pending, (state) => {
        state.checkoutLoading = true
      })
      .addCase(startCheckout.fulfilled, (state) => {
        state.checkoutLoading = false
      })
      .addCase(startCheckout.rejected, (state) => {
        state.checkoutLoading = false
      })
      .addCase(changePlan.pending, (state) => {
        state.updatingPlan = true
      })
      .addCase(changePlan.fulfilled, (state, action) => {
        state.updatingPlan = false
        if (action.payload) state.subscription = action.payload
      })
      .addCase(changePlan.rejected, (state) => {
        state.updatingPlan = false
      })
      .addCase(cancelMySubscription.pending, (state) => {
        state.cancelling = true
      })
      .addCase(cancelMySubscription.fulfilled, (state, action) => {
        state.subscription = action.payload
        state.cancelling = false
      })
      .addCase(cancelMySubscription.rejected, (state) => {
        state.cancelling = false
      })
      .addCase(reactivateMySubscription.pending, (state) => {
        state.reactivating = true
      })
      .addCase(reactivateMySubscription.fulfilled, (state, action) => {
        state.subscription = action.payload
        state.reactivating = false
      })
      .addCase(reactivateMySubscription.rejected, (state) => {
        state.reactivating = false
      })
      .addCase(bookCareerExpertSession.pending, (state) => {
        state.bookingCareerExpertSession = true
      })
      .addCase(bookCareerExpertSession.fulfilled, (state, action) => {
        state.bookingCareerExpertSession = false
        if (action.payload) {
          state.lastCareerExpertBooking = action.payload
          state.careerExpertBookings = [action.payload, ...state.careerExpertBookings]
        }
      })
      .addCase(bookCareerExpertSession.rejected, (state) => {
        state.bookingCareerExpertSession = false
      })
      .addCase(fetchMyCareerExpertBookings.pending, (state) => {
        state.careerExpertBookingsLoading = true
      })
      .addCase(fetchMyCareerExpertBookings.fulfilled, (state, action) => {
        state.careerExpertBookings = action.payload
        state.careerExpertBookingsLoading = false
      })
      .addCase(fetchMyCareerExpertBookings.rejected, (state) => {
        state.careerExpertBookingsLoading = false
      })
      .addCase(openBillingPortal.pending, (state) => {
        state.portalLoading = true
      })
      .addCase(openBillingPortal.fulfilled, (state) => {
        state.portalLoading = false
      })
      .addCase(openBillingPortal.rejected, (state) => {
        state.portalLoading = false
      })
  },
  initialState,
  name: 'subscription',
  reducers: {},
})

interface RootStateSlice {
  subscription: SubscriptionState
}

export const selectPlans = (state: RootStateSlice): Plan[] => state.subscription.plans
export const selectPlansLoading = (state: RootStateSlice): boolean => state.subscription.plansLoading
export const selectMySubscription = (state: RootStateSlice): Subscription | null => state.subscription.subscription
export const selectSubscriptionLoading = (state: RootStateSlice): boolean => state.subscription.subscriptionLoading
export const selectSubscriptionLimits = (state: RootStateSlice): SubscriptionLimits | null => state.subscription.limits
export const selectLimitsLoading = (state: RootStateSlice): boolean => state.subscription.limitsLoading
export const selectCheckoutLoading = (state: RootStateSlice): boolean => state.subscription.checkoutLoading
export const selectUpdatingPlan = (state: RootStateSlice): boolean => state.subscription.updatingPlan
export const selectBookingCareerExpertSession = (state: RootStateSlice): boolean => state.subscription.bookingCareerExpertSession
export const selectLastCareerExpertBooking = (state: RootStateSlice): CareerExpertBooking | null => state.subscription.lastCareerExpertBooking
export const selectCareerExpertBookings = (state: RootStateSlice): CareerExpertBooking[] => state.subscription.careerExpertBookings
export const selectCareerExpertBookingsLoading = (state: RootStateSlice): boolean => state.subscription.careerExpertBookingsLoading
export const selectCancelling = (state: RootStateSlice): boolean => state.subscription.cancelling
export const selectReactivating = (state: RootStateSlice): boolean => state.subscription.reactivating
export const selectPortalLoading = (state: RootStateSlice): boolean => state.subscription.portalLoading

export default subscriptionSlice.reducer
